/**
 * Token Manager
 * Handles OAuth2/DPoP token lifecycle with proactive refresh strategy
 */

export type TokenData = {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number; // Unix timestamp in seconds
  catToken?: string;
}

export type TokenManagerConfig = {
  tokenEndpoint: string;
  refreshEndpoint?: string;
  refreshThresholdSeconds?: number; // Refresh when token expires in less than this (default: 300 = 5 min)
}

/**
 * Manages OAuth2 access tokens with proactive refresh strategy
 * Prevents failed API calls due to expired tokens
 */
// import { indexDBStorage } from '../storage/indexDBStorage';
import { generateDPoPProof, getDPoPNonce, storeDPoPNonce } from './fetchService';
import { dpopLog, dpopWarn } from './dpopLogger';
// import { StoreName } from '../typings/StorageTypes';

const LOG_PREFIX = '[TokenManager]';
// const STORAGE_KEY_ACCESS = 'dpop_access_token';
// const STORAGE_KEY_REFRESH = 'dpop_refresh_token';
// const STORAGE_KEY_EXPIRY = 'dpop_token_expiry';
// const STORAGE_KEY_CAT_DATA = 'dpop_cat_token';
// // Key pair keys must match those used in fetchService to ensure coordinated cleanup
// const STORAGE_KEY_DPOP_PRIVATE = 'dpop_private_key';
// const STORAGE_KEY_DPOP_PUBLIC = 'dpop_public_key';
// const STORE_NAME: StoreName = 'adp-ndt-session-references';

// In-memory storage for DPoP values (IndexDB persistence commented out)
let _inMemoryTokens: TokenData | null = null;
let _inMemoryKeyPair: CryptoKeyPair | null = null;

function parseJWTExpiry(token: string): number {
  dpopLog(LOG_PREFIX, 'parseJWTExpiry: parsing token expiry');
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }
    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) {
      // No expiry - default to 1 hour from now
      dpopLog(LOG_PREFIX, 'parseJWTExpiry: no exp claim found, defaulting to 1 hour');
      return Math.floor(Date.now() / 1000) + 3600;
    }
    dpopLog(LOG_PREFIX, 'parseJWTExpiry: token expires at', new Date(payload.exp * 1000).toISOString());
    return payload.exp;
  } catch (error) {
    dpopWarn(LOG_PREFIX, 'parseJWTExpiry: failed to parse JWT expiry, defaulting to 1 hour:', error);
    return Math.floor(Date.now() / 1000) + 3600;
  }
}

async function loadTokensFromStorage(): Promise<TokenData | null> {
  dpopLog(LOG_PREFIX, 'loadTokensFromStorage: loading tokens from in-memory store');
  // try {
  //   await indexDBStorage.getInstance(); // Ensure DB is initialized
  //   const accessToken = await indexDBStorage.transactions.get(STORE_NAME, STORAGE_KEY_ACCESS);
  //   const refreshToken = await indexDBStorage.transactions.get(STORE_NAME, STORAGE_KEY_REFRESH);
  //   const expiryStr = await indexDBStorage.transactions.get(STORE_NAME, STORAGE_KEY_EXPIRY);
  //   const catToken = await indexDBStorage.transactions.get(STORE_NAME, STORAGE_KEY_CAT_DATA);
  //   const expiresAt = expiryStr ? parseInt(expiryStr, 10) : null;
  //   if (accessToken && expiresAt) {
  //     console.log(LOG_PREFIX, 'loadTokensFromStorage: tokens found in storage');
  //     return { accessToken, refreshToken, expiresAt, catToken: catToken ?? undefined };
  //   }
  //   console.log(LOG_PREFIX, 'loadTokensFromStorage: no valid tokens in storage');
  //   return null;
  // } catch (error) {
  //   console.warn(LOG_PREFIX, 'loadTokensFromStorage: storage error, treating as no tokens:', error);
  //   return null;
  // }
  if (_inMemoryTokens) {
    dpopLog(LOG_PREFIX, 'loadTokensFromStorage: tokens found in memory');
  } else {
    dpopLog(LOG_PREFIX, 'loadTokensFromStorage: no valid tokens in memory');
  }
  return _inMemoryTokens;
}

async function saveTokensToStorage(tokenData: TokenData): Promise<void> {
  dpopLog(LOG_PREFIX, 'saveTokensToStorage: saving tokens to in-memory store');
  // try {
  //   await indexDBStorage.getInstance(); // Ensure DB is initialized
  //   await indexDBStorage.transactions.put(STORE_NAME, tokenData.accessToken, STORAGE_KEY_ACCESS);
  //   if (tokenData.refreshToken) {
  //     await indexDBStorage.transactions.put(STORE_NAME, tokenData.refreshToken, STORAGE_KEY_REFRESH);
  //   }
  //   await indexDBStorage.transactions.put(STORE_NAME, tokenData.expiresAt.toString(), STORAGE_KEY_EXPIRY);
  //   if (tokenData.catToken) {
  //     await indexDBStorage.transactions.put(STORE_NAME, tokenData.catToken, STORAGE_KEY_CAT_DATA);
  //   }
  //   console.log(LOG_PREFIX, 'saveTokensToStorage: tokens saved successfully');
  // } catch (error) {
  //   console.warn(LOG_PREFIX, 'saveTokensToStorage: failed to save tokens to storage:', error);
  // }
  _inMemoryTokens = tokenData;
  dpopLog(LOG_PREFIX, 'saveTokensToStorage: tokens saved to memory successfully');
}

async function clearTokensFromStorage(): Promise<void> {
  dpopLog(LOG_PREFIX, 'clearTokensFromStorage: clearing tokens from in-memory store');
  // try {
  //   await indexDBStorage.getInstance(); // Ensure DB is initialized
  //   await indexDBStorage.transactions.delete(STORE_NAME, STORAGE_KEY_ACCESS);
  //   await indexDBStorage.transactions.delete(STORE_NAME, STORAGE_KEY_REFRESH);
  //   await indexDBStorage.transactions.delete(STORE_NAME, STORAGE_KEY_EXPIRY);
  //   await indexDBStorage.transactions.delete(STORE_NAME, STORAGE_KEY_CAT_DATA);
  //   // DPoP key pair is intentionally preserved - it is reused across token refreshes
  //   // and only cleared explicitly via TokenManager.clearKeyPair() or clearDPoPKeyPair()
  //   console.log(LOG_PREFIX, 'clearTokensFromStorage: tokens cleared');
  // } catch (error) {
  //   console.warn(LOG_PREFIX, 'clearTokensFromStorage: failed to clear tokens from storage:', error);
  // }
  _inMemoryTokens = null;
  dpopLog(LOG_PREFIX, 'clearTokensFromStorage: tokens cleared from memory');
}

type TokenEndpointResponse = {
  access_token: string;
  refresh_token?: string;
  cat_token?: string;
}

const TOKEN_REQUEST_TIMEOUT_MS = 10000; // 10 s — token endpoint should respond well within this window

async function requestToken(endpoint: string, errorPrefix: string, keyPair: CryptoKeyPair): Promise<TokenEndpointResponse> {
  dpopLog(LOG_PREFIX, `requestToken: sending ${errorPrefix} request to`, endpoint);
  const httpMethod = 'POST';
  // Normalize htu to scheme + host + path only — no query string or fragment (RFC 9449 §4.2)
  const endpointUrl = new URL(endpoint);
  const htu = `${endpointUrl.origin}${endpointUrl.pathname}`;

  /**
   * Performs a single timed fetch to the token endpoint, generating a fresh DPoP proof
   * on each invocation so any nonce stored from a preceding error response is automatically
   * included when this helper is called again on the nonce retry.
   */
  const attemptFetch = async (): Promise<Response> => {
    const nonce = getDPoPNonce(endpointUrl.origin);
    const dpopProof = await generateDPoPProof(httpMethod, htu, keyPair, undefined, nonce);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TOKEN_REQUEST_TIMEOUT_MS);

    try {
      return await fetch(endpoint, {
        method: httpMethod,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'DPoP': dpopProof
        },
        body: JSON.stringify({
          entity_type: "download-center",
          entity_id: crypto.randomUUID(),
          scope: "ndt:upload ndt:write"
        })
      });
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error(`${errorPrefix}: request timed out after ${TOKEN_REQUEST_TIMEOUT_MS}ms`);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  try {
    let response = await attemptFetch();

    // RFC 9449 §8 — always store any DPoP-Nonce the server returns (success or error).
    const serverNonce = response.headers.get('DPoP-Nonce');
    if (serverNonce) {
      storeDPoPNonce(endpointUrl.origin, serverNonce);
      dpopLog(LOG_PREFIX, 'requestToken: DPoP-Nonce received, stored for future requests');
    }

    // If the server returned an error alongside a nonce (e.g. use_dpop_nonce 400),
    // retry once — the nonce is now in the store and will be included in the fresh proof.
    if (!response.ok && serverNonce) {
      dpopLog(LOG_PREFIX, 'requestToken: retrying with server-provided DPoP nonce');
      response = await attemptFetch();
      const rotatedNonce = response.headers.get('DPoP-Nonce');
      if (rotatedNonce) storeDPoPNonce(endpointUrl.origin, rotatedNonce);
    }

    if (!response.ok) {
      console.error(LOG_PREFIX, `requestToken: ${errorPrefix} - ${response.status} ${response.statusText}`);
      throw new Error(`${errorPrefix}: ${response.status} ${response.statusText}`);
    }

    dpopLog(LOG_PREFIX, 'requestToken: token received successfully');
    return await response.json();
  } catch (error: any) {
    console.error(LOG_PREFIX, `requestToken: ${errorPrefix} failed:`, error.message);
    throw error;
  }
}


export const TokenManager = {
  /**
   * Load the persisted DPoP key pair from IndexDB.
   * Returns null if none is stored or if import fails.
   */
  loadKeyPair: async (): Promise<CryptoKeyPair | null> => {
    dpopLog(LOG_PREFIX, 'loadKeyPair: loading DPoP key pair from in-memory store');
    // try {
    //   await indexDBStorage.getInstance();
    //   const privateJwkStr = await indexDBStorage.transactions.get(STORE_NAME, STORAGE_KEY_DPOP_PRIVATE);
    //   const publicJwkStr = await indexDBStorage.transactions.get(STORE_NAME, STORAGE_KEY_DPOP_PUBLIC);
    //   if (!privateJwkStr || !publicJwkStr) {
    //     console.log(LOG_PREFIX, 'loadKeyPair: no key pair found in storage');
    //     return null;
    //   }
    //   const privateKey = await crypto.subtle.importKey(
    //     'jwk', JSON.parse(privateJwkStr), { name: 'ECDSA', namedCurve: 'P-256' }, true, ['sign']
    //   );
    //   const publicKey = await crypto.subtle.importKey(
    //     'jwk', JSON.parse(publicJwkStr), { name: 'ECDSA', namedCurve: 'P-256' }, true, ['verify']
    //   );
    //   console.log(LOG_PREFIX, 'loadKeyPair: DPoP key pair loaded successfully');
    //   return { privateKey, publicKey };
    // } catch (error) {
    //   console.warn(LOG_PREFIX, 'loadKeyPair: failed to load or import key pair, will regenerate:', error);
    //   return null;
    // }
    if (_inMemoryKeyPair) {
      dpopLog(LOG_PREFIX, 'loadKeyPair: DPoP key pair loaded from memory successfully');
    } else {
      dpopLog(LOG_PREFIX, 'loadKeyPair: no key pair found in memory');
    }
    return _inMemoryKeyPair;
  },
  /**
   * Persist the DPoP key pair to IndexDB alongside the tokens.
   */
  saveKeyPair: async (keyPair: CryptoKeyPair): Promise<void> => {
    dpopLog(LOG_PREFIX, 'saveKeyPair: saving DPoP key pair to in-memory store');
    // try {
    //   await indexDBStorage.getInstance();
    //   const privateJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey);
    //   const publicJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
    //   await indexDBStorage.transactions.put(STORE_NAME, JSON.stringify(privateJwk), STORAGE_KEY_DPOP_PRIVATE);
    //   await indexDBStorage.transactions.put(STORE_NAME, JSON.stringify(publicJwk), STORAGE_KEY_DPOP_PUBLIC);
    //   console.log(LOG_PREFIX, 'saveKeyPair: DPoP key pair saved successfully');
    // } catch (error) {
    //   console.warn(LOG_PREFIX, 'saveKeyPair: failed to save DPoP key pair to storage:', error);
    // }
    _inMemoryKeyPair = keyPair;
    dpopLog(LOG_PREFIX, 'saveKeyPair: DPoP key pair saved to memory successfully');
  },
  /**
   * Remove the persisted DPoP key pair from IndexDB.
   */
  clearKeyPair: async (): Promise<void> => {
    dpopLog(LOG_PREFIX, 'clearKeyPair: clearing DPoP key pair from in-memory store');
    // try {
    //   await indexDBStorage.getInstance();
    //   await indexDBStorage.transactions.delete(STORE_NAME, STORAGE_KEY_DPOP_PRIVATE);
    //   await indexDBStorage.transactions.delete(STORE_NAME, STORAGE_KEY_DPOP_PUBLIC);
    //   console.log(LOG_PREFIX, 'clearKeyPair: DPoP key pair cleared');
    // } catch (error) {
    //   console.warn(LOG_PREFIX, 'clearKeyPair: failed to clear DPoP key pair from storage:', error);
    // }
    _inMemoryKeyPair = null;
    dpopLog(LOG_PREFIX, 'clearKeyPair: DPoP key pair cleared from memory');
  },
  getValidToken: async (config: TokenManagerConfig, keyPair: CryptoKeyPair) => {
    dpopLog(LOG_PREFIX, 'getValidToken: checking token validity');
    const refreshThresholdSeconds = config.refreshThresholdSeconds || 300;
    const tokens = await loadTokensFromStorage();
    if (!tokens) {
      dpopLog(LOG_PREFIX, 'getValidToken: no tokens found, creating new token');
      return await TokenManager.createToken(config, keyPair);
    }
    const now = Math.floor(Date.now() / 1000);
    const expiresIn = tokens.expiresAt - now;
    if (expiresIn > refreshThresholdSeconds) {
      dpopLog(LOG_PREFIX, `getValidToken: token is valid, expires in ${expiresIn}s`);
      return tokens.accessToken;
    }
    // Token is expiring soon or expired - refresh proactively
    dpopLog(LOG_PREFIX, `getValidToken: token expiring soon (${expiresIn}s), refreshing proactively`);
    return await TokenManager.refreshToken(config, keyPair);
  },
  getValidCCat: async (config: TokenManagerConfig, keyPair: CryptoKeyPair) => {
    dpopLog(LOG_PREFIX, 'getValidCCat: checking for valid c_cat');
    const tokens = await loadTokensFromStorage();
    if (!tokens) {
      dpopLog(LOG_PREFIX, 'getValidCCat: no tokens found, creating new token');
      await TokenManager.createToken(config, keyPair);
      const newTokens = await loadTokensFromStorage();
      return newTokens?.catToken ?? null;
    }
    const now = Math.floor(Date.now() / 1000);
    const expiresIn = tokens.expiresAt - now;
    const refreshThresholdSeconds = config.refreshThresholdSeconds || 300;
    if (expiresIn > refreshThresholdSeconds) {
      dpopLog(LOG_PREFIX, 'getValidCCat: returning stored c_cat');
      return tokens.catToken ?? null;
    }
    dpopLog(LOG_PREFIX, `getValidCCat: token expiring soon (${expiresIn}s), refreshing proactively`);
    await TokenManager.refreshToken(config, keyPair);
    const refreshedTokens = await loadTokensFromStorage();
      return refreshedTokens?.catToken ?? null;
  },
  setTokens: async (tokenData: TokenData) => {
    dpopLog(LOG_PREFIX, 'setTokens: storing token data');
    await saveTokensToStorage(tokenData);
  },
  setTokensFromJWT: async (accessToken: string, refreshToken?: string, catToken?: string) => {
    dpopLog(LOG_PREFIX, 'setTokensFromJWT: parsing and storing tokens from JWT');
    const expiry = parseJWTExpiry(accessToken);
    await TokenManager.setTokens({ accessToken, refreshToken, expiresAt: expiry, catToken });
  },
  clearTokens: async () => {
    dpopLog(LOG_PREFIX, 'clearTokens: clearing tokens');
    await clearTokensFromStorage();
  },
  hasToken: async () => {
    dpopLog(LOG_PREFIX, 'hasToken: checking if token exists in storage');
    const tokens = await loadTokensFromStorage();
    dpopLog(LOG_PREFIX, 'hasToken:', !!tokens);
    return !!tokens;
  },
  getTokenInfo: async () => {
    dpopLog(LOG_PREFIX, 'getTokenInfo: fetching token info');
    const tokens = await loadTokensFromStorage();
    if (!tokens) {
      dpopLog(LOG_PREFIX, 'getTokenInfo: no token found');
      return { hasToken: false, expiresIn: null };
    }
    const now = Math.floor(Date.now() / 1000);
    const expiresIn = tokens.expiresAt - now;
    dpopLog(LOG_PREFIX, `getTokenInfo: token found, expires in ${expiresIn}s`);
    return { hasToken: true, expiresIn };
  },
  createToken: async (config: TokenManagerConfig, keyPair: CryptoKeyPair) => {
    dpopLog(LOG_PREFIX, 'createToken: requesting new token from', config.tokenEndpoint);
    try {
      const data = await requestToken(config.tokenEndpoint, 'Token creation failed', keyPair);
      await TokenManager.setTokensFromJWT(data.access_token, data.refresh_token, data.cat_token);
      dpopLog(LOG_PREFIX, 'createToken: token created and stored successfully');
      return data.access_token;
    } catch (error) {
      console.error(LOG_PREFIX, 'createToken: failed to create token, clearing storage:', error);
      await clearTokensFromStorage();
      throw error;
    }
  },
  refreshToken: async (config: TokenManagerConfig, keyPair: CryptoKeyPair) => {
    dpopLog(LOG_PREFIX, 'refreshToken: attempting token refresh');
    const tokens = await loadTokensFromStorage();
    if (!tokens || !tokens.refreshToken) {
      dpopLog(LOG_PREFIX, 'refreshToken: no refresh token available, creating new token instead');
      await clearTokensFromStorage();
      return await TokenManager.createToken(config, keyPair);
    }
    try {
      const endpoint = config.refreshEndpoint || config.tokenEndpoint;
      dpopLog(LOG_PREFIX, 'refreshToken: refreshing token via', endpoint);
      const data = await requestToken(endpoint, 'Token refresh failed', keyPair);
      await TokenManager.setTokensFromJWT(data.access_token, data.refresh_token || tokens?.refreshToken, data.cat_token);
      dpopLog(LOG_PREFIX, 'refreshToken: token refreshed and stored successfully');
      return data.access_token;
    } catch (error) {
      console.error(LOG_PREFIX, 'refreshToken: failed to refresh token, clearing storage:', error);
      await clearTokensFromStorage();
      throw error;
    }
  }
};



// No singleton or class export; all functions are pure and stateless, using indexDBStorage.
